import React, { useState, useEffect } from 'react';

const AnalyticsAuth = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Check if already authenticated on mount
  useEffect(() => {
    const authToken = localStorage.getItem('analytics_auth');
    const authTime = localStorage.getItem('analytics_auth_time');
    
    if (authToken && authTime) {
      const hoursSinceAuth = (Date.now() - parseInt(authTime)) / (1000 * 60 * 60);
      // Session expires after 24 hours
      if (hoursSinceAuth < 24) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('analytics_auth');
        localStorage.removeItem('analytics_auth_time');
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // In development, use a simple password
      const devPassword = 'analytics2024';
      
      // In production, check against environment variable or API
      let correctPassword = devPassword;
      
      if (process.env.NODE_ENV === 'production') {
        // Try to fetch the password from your API endpoint
        try {
          const response = await fetch('/api/auth-check', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password })
          });
          
          if (response.ok) {
            correctPassword = password; // If API validates, accept it
          } else {
            throw new Error('Invalid password');
          }
        } catch (apiError) {
          // Fallback: use a hardcoded hash for production
          // You can change this to your own secure password
          const productionPasswordHash = 'your-secure-password-here';
          if (password !== productionPasswordHash) {
            throw new Error('Invalid password');
          }
        }
      } else {
        // Development mode - simple password check
        if (password !== correctPassword) {
          throw new Error('Invalid password');
        }
      }

      // Store authentication
      localStorage.setItem('analytics_auth', 'authenticated');
      localStorage.setItem('analytics_auth_time', Date.now().toString());
      setIsAuthenticated(true);
      
    } catch (error) {
      setError('Invalid password. Access denied.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('analytics_auth');
    localStorage.removeItem('analytics_auth_time');
    setIsAuthenticated(false);
    setPassword('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Analytics Dashboard
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              This area is password protected
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            
            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Checking...' : 'Access Dashboard'}
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-500 text-center bg-yellow-50 p-3 rounded-lg">
                <strong>Development Mode:</strong> Password is "analytics2024"
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Logout button */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={handleLogout}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg"
        >
          Logout
        </button>
      </div>
      {children}
    </div>
  );
};

export default AnalyticsAuth; 